import { useEffect, useState } from 'react';

/**
 * Abaixo disso a redução da viewport visual é a barra de URL do browser
 * se recolhendo, não o teclado. Teclado virtual em celular nunca ocupa
 * menos que ~200px; 120px é folga confortável entre os dois casos.
 */
export const LIMIAR_TECLADO_PX = 120;

/**
 * Geometria do teclado virtual, publicada como CSS vars no <html>:
 *
 * - `--kb-inset`  — px cobertos pelo teclado (`0px` quando fechado)
 * - `--vv-height` — altura visível real em px
 *
 * São CSS vars e não estado React de propósito: o listener de
 * `visualViewport` dispara a cada frame enquanto o teclado anima, e
 * escrever numa CSS var não causa re-render. Só o booleano de retorno
 * entra no ciclo do React, e ele muda uma vez por abertura (React faz
 * bail-out quando o `setState` recebe o mesmo valor).
 *
 * O Chrome resolve boa parte disso sozinho via
 * `interactive-widget=resizes-content` no <meta viewport>; este hook é o
 * que cobre o Safari iOS, que ignora essa flag.
 */
export function useTecladoVirtual(): boolean {
  const [tecladoAberto, setTecladoAberto] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;

    // Browser sem visualViewport: valores neutros, comportamento idêntico
    // ao de antes deste hook existir.
    if (!vv) {
      root.style.setProperty('--kb-inset', '0px');
      root.style.setProperty('--vv-height', '100dvh');
      return;
    }

    const medir = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--kb-inset', `${inset}px`);
      root.style.setProperty('--vv-height', `${vv.height}px`);
      setTecladoAberto(inset > LIMIAR_TECLADO_PX);
    };

    medir();
    vv.addEventListener('resize', medir);
    vv.addEventListener('scroll', medir);

    return () => {
      vv.removeEventListener('resize', medir);
      vv.removeEventListener('scroll', medir);
      root.style.removeProperty('--kb-inset');
      root.style.removeProperty('--vv-height');
    };
  }, []);

  return tecladoAberto;
}
